import { type JSX } from 'solid-js'
import type {
  EllipseNode,
  ImageNode,
  LineNode,
  PathNode,
  RectNode,
  SceneNode,
} from '../../scene/types.ts'
import { polylineD, smoothStrokeD } from '../../util/path.ts'
import { assetSrc } from '../../localAssets.ts'
import { TextView } from './TextView.tsx'

/** Transparent stroke width, in world px, that makes thin geometry easy to hit. */
const HIT_WIDTH = 12

function RectView(props: { node: RectNode }) {
  return (
    <svg class="shape" width="100%" height="100%" overflow="visible">
      <rect
        x={0}
        y={0}
        width={props.node.w}
        height={props.node.h}
        rx={props.node.cornerRadius}
        ry={props.node.cornerRadius}
        fill={props.node.fill}
        stroke={props.node.stroke}
        stroke-width={props.node.strokeWidth}
      />
    </svg>
  )
}

function EllipseView(props: { node: EllipseNode }) {
  return (
    <svg class="shape" width="100%" height="100%" overflow="visible">
      <ellipse
        cx={props.node.w / 2}
        cy={props.node.h / 2}
        rx={props.node.w / 2}
        ry={props.node.h / 2}
        fill={props.node.fill}
        stroke={props.node.stroke}
        stroke-width={props.node.strokeWidth}
      />
    </svg>
  )
}

/**
 * Lines and freehand paths share a renderer: local points inside a fixed
 * viewBox, stretched to the node's current size. `preserveAspectRatio="none"`
 * is what makes resize work without rewriting any point.
 */
function StrokeView(props: { node: LineNode | PathNode; smooth: boolean }) {
  const d = () =>
    props.smooth
      ? smoothStrokeD(props.node.points)
      : polylineD(props.node.points, props.node.type === 'line' && props.node.closed)

  // The hit stroke is specified in viewBox units, so convert from world px.
  const hitWidth = () =>
    Math.max(
      props.node.strokeWidth,
      (HIT_WIDTH * props.node.viewW) / Math.max(1, props.node.w),
    )

  return (
    <svg
      class="shape"
      width="100%"
      height="100%"
      viewBox={`0 0 ${props.node.viewW} ${props.node.viewH}`}
      preserveAspectRatio="none"
      overflow="visible"
    >
      <path
        d={d()}
        fill="none"
        stroke="transparent"
        stroke-width={hitWidth()}
        stroke-linecap="round"
        // A filled shape is hit anywhere inside, a line only along its stroke.
        pointer-events={props.node.type === 'line' && props.node.closed ? 'all' : 'stroke'}
      />
      <path
        d={d()}
        fill={(props.node.type === 'line' && props.node.closed && props.node.fill) || 'none'}
        stroke={props.node.stroke}
        stroke-width={props.node.strokeWidth}
        stroke-linecap="round"
        stroke-linejoin="round"
        pointer-events="none"
      />
    </svg>
  )
}

function ImageView(props: { node: ImageNode }) {
  return (
    <img
      class="shape"
      src={assetSrc(props.node.assetId)}
      alt=""
      draggable={false}
      width={props.node.w}
      height={props.node.h}
    />
  )
}

function body(node: SceneNode): JSX.Element {
  // A node's type never changes, so dispatching once here is correct and keeps
  // every property read inside the leaf component fine-grained.
  switch (node.type) {
    case 'rect':
      return <RectView node={node} />
    case 'ellipse':
      return <EllipseView node={node} />
    case 'line':
      return <StrokeView node={node} smooth={false} />
    case 'path':
      return <StrokeView node={node} smooth={true} />
    case 'image':
      return <ImageView node={node} />
    case 'text':
      return <TextView node={node} />
  }
}

export function NodeView(props: { node: SceneNode }) {
  return (
    <div
      class="node"
      data-node-id={props.node.id}
      style={{
        transform: `translate(${props.node.x}px, ${props.node.y}px) rotate(${props.node.rotation}rad)`,
        width: `${props.node.w}px`,
        height: `${props.node.h}px`,
        opacity: props.node.opacity,
        display: props.node.hidden ? 'none' : 'block',
      }}
    >
      {body(props.node)}
    </div>
  )
}
